<template>
  <div class="audio-recorder-container mt-3">
    <!-- Demo Banner -->
    <div
      v-if="showDemo"
      class="
        d-flex
        flex-column flex-md-row flex-wrap
        justify-content-center justify-content-md-between
        align-items-center
        badge badge-primary-light
        relative-position
      "
    >
      <button class="close close-demo text-dark" @click="closeDemo">
        &times;
      </button>
      <div
        class="
          d-flex
          flex-wrap flex-column flex-md-row
          align-items-center
          mb-3 mb-md-0
          justify-content-center justify-content-md-start
        "
      >
        <div class="demo-image-container mr-md-4 mb-2 mb-md-0">
          <i class="fas fa-2x fa-file-video demo-img"></i>
        </div>
        <div class="text-md-left demo-text">
          <h4 class="font-weight-bold text-black">
            Watch a demo of how to use the AI Scribe inside C3
          </h4>
          <p class="mb-0 powered-by">
            AI Scribe powered by
            <a
              target="_blank"
              href="https://onechart.ai"
              class="link-color font-weight-bold"
              >One</a
            >
            <span class="text-black"> Chart</span>
          </p>
        </div>
      </div>
      <button
        class="btn btn-primary scribe-action-btn"
        v-if="demoLink"
        @click="watchDemo"
      >
        <i class="fas fa-file-video mr-2"></i>Watch Demo
      </button>
    </div>

    <div
      class="d-flex justify-content-end align-items-center flex-wrap"
      v-if="notesInfo.remaining < 10"
    >
      <div
        class="
          alert
          alert5-warning
          mt-3
          mb-0
          py-2
          d-flex
          align-items-center
          px-3
        "
      >
        <badge class="badge-warning rounded-circle font-weight-bold mr-2">{{
          notesInfo.remaining
        }}</badge>
        Limited note{{ notesInfo.remaining === 1 ? "" : "s" }} remaining
      </div>
      <button
        class="btn btn-black ml-3 mt-3 py-2"
        @click="openScribeUpgradeAIModal"
      >
        Upgrade?
      </button>
    </div>
    <div
      class="d-flex flex-wrap mt-3 justify-content-between align-items-center"
      v-if="
        !isRecording &&
        !isPaused &&
        !audioURL &&
        !audioRecorded &&
        !meetingConfirmed
      "
    >
      <div class="col-auto px-0">
        <div
          class="
            custom-control custom-radio
            mr-4
            d-flex
            col-12
            align-items-center
            flex-wrap
          "
        >
          <input
            type="radio"
            id="audio-record"
            value="record"
            v-model="uploadType"
            class="custom-control-input"
          />
          <label class="custom-control-label col-auto" for="audio-record"
            >Record Audio</label
          >
          <select-control
            v-model="selectedDeviceId"
            :options="audioDevices"
            :disabled="isRecording || isPaused || !notesInfo.remaining"
            type="horizontal"
            wrapClass="mb-0"
            selectClass="min-296"
            labelClass="d-none"
            inputClass="light-shadow"
          >
            Select Microphone:
          </select-control>
        </div>
        <div
          class="
            custom-control custom-radio
            mr-4
            d-flex
            col-12
            align-items-center
            flex-wrap
          "
        >
          <input
            type="radio"
            id="audio-file"
            value="file"
            v-model="uploadType"
            class="custom-control-input"
          />
          <label
            class="custom-control-label col-auto mr-2 mt-2"
            for="audio-file"
            >Upload MP3</label
          >
          <div class="custom-file">
            <input
              class="custom-file-input pointer-cursor"
              id="upload-audio-input"
              type="file"
              accept="audio/mpeg"
              @change="updateSelectedAudioFile"
            />
            <label
              class="custom-file-label radius-16 ellipsis file-name-label mt-2"
              :class="{ 'text-muted': !externalAudioFile }"
              for="upload-audio-input"
              >{{
                externalAudioFile ? externalAudioFile.name : "Select MP3 File"
              }}</label
            >
          </div>
          <audio
            v-if="externalAudioUrl"
            :src="externalAudioUrl"
            controls
            class="maxw-100 border mt-2 external-audio-result radius-24"
          >
            Processing
          </audio>
        </div>
        <div
          class="
            custom-control custom-radio
            mr-4
            d-flex
            col-12
            align-items-center
            flex-wrap
            mt-2
          "
          v-if="meetingId"
        >
          <input
            type="radio"
            id="meeting-audio-file"
            value="meeting"
            v-model="uploadType"
            class="custom-control-input"
          />
          <label
            class="custom-control-label col-auto mr-2 mt-2"
            for="meeting-audio-file"
            >Use Meeting Record</label
          >
          <div class="small pl-2 ml-2 w-100 font-weight-bold">
            <p class="mb-0">Please make sure to:</p>
            <ul>
              <li>Start your video recording before you begin the scribe.</li>
              <li>
                Stop your video recording before completing the scribe.<br />Any
                running recordings will be discarded.
              </li>
            </ul>
          </div>
        </div>
      </div>
      <button
        class="btn btn-e27c1d mt-3 mt-sm-0 mr-3 mr-sm-0 scribe-action-btn"
        @click="checkScribeAiTerms"
      >
        {{ meetingId && uploadType === "meeting" ? "Use" : "Start" }} AI Scribe
      </button>
    </div>

    <!-- Recording Controls -->
    <div
      class="
        d-flex
        mt-3
        pb-3
        badge
        controls-badge
        justify-content-between
        align-items-center
        flex-wrap
        w-100
      "
      v-if="isRecording || isPaused || audioRecorded || meetingConfirmed"
    >
      <div
        class="d-flex align-items-center mt-3 flex-wrap selects-outer-container"
      >
        <div
          v-if="isRecording || isPaused"
          class="recording-status d-flex align-items-end mr-4"
        >
          <span
            class="recording-dot mr-2"
            :class="{ 'recording-active': isRecording }"
          ></span>
          <strong
            class="mr-3"
            v-html="isPaused ? '&nbsp;Stopped&nbsp;&nbsp;' : 'Recording'"
          ></strong>
          <div
            class="signal-bars d-flex"
            :class="{ 'recording-active': isRecording }"
          >
            <span v-for="i in 5" :key="i" class="bar"></span>
          </div>
        </div>
        <select-control
          v-if="isRecording || isPaused || audioRecorded || meetingConfirmed"
          v-model="selectedDocument"
          :options="documentOptions"
          type="horizontal"
          selectClass="col-12 px-0 col-sm-auto mt-2 mt-sm-0 doc-type-select"
          wrapClass="mb-0 px-0 mr-md-2"
          labelClass="d-none"
          inputClass=""
        >
          Select Type:
        </select-control>

        <select-control
          v-if="isRecording || isPaused || audioRecorded || meetingConfirmed"
          v-model="selectedLanguage"
          :options="languageOptions"
          type="horizontal"
          selectClass="col-12 px-0 col-sm-auto mt-2 mt-sm-0 lang-select"
          wrapClass="mb-0 px-0"
          labelClass="d-none"
          inputClass=""
        >
          Select Language:
        </select-control>

        <!-- Audio Playback -->
        <div
          class="
            text-left
            ml-0 ml-md-3
            mt-2 mt-lg-0
            audio-recorder-outer-container
          "
          v-if="audioRecorded"
        >
          <alert
            class="
              alert
              light-shadow
              d-inline-block
              mb-0
              fa-lg
              px-5
              audio-recorder-container
            "
            color="gray"
            :spinner="true"
            v-if="audioRecorded && !audioURL"
          >
            Record Processing...
          </alert>
          <audio
            v-if="audioURL"
            :src="audioURL"
            controls
            class="maxw-100 radius-24 audio-recorder-container"
            style="height: 38px"
          >
            Processing
          </audio>
        </div>
      </div>

      <div class="d-flex justify-content-end align-items-start mt-3">
        <button
          class="btn btn-black"
          @click="pauseRecording"
          v-if="isRecording && !isPaused"
        >
          Pause Scribe
        </button>
        <button class="btn btn-black" @click="resumeRecording" v-if="isPaused">
          Resume Scribe
        </button>
        <button
          class="btn btn-deep-success ml-2"
          :disabled="disabled"
          @click="stopRecording"
          v-if="isRecording || isPaused"
        >
          Complete
        </button>
        <button
          class="btn btn-deep-success scribe-complete-action-btn"
          :disabled="disabled"
          @click="completeScribe"
          v-if="(audioRecorded && audioURL && !audioQueued) || meetingConfirmed"
        >
          Complete Scribe
        </button>
      </div>
      <div
        ref="visualizerContainer"
        v-if="isRecording || isPaused"
        class="audio-visualizer mt-3"
      ></div>
    </div>

    <!-- Terms Modal -->
    <modal
      class="scribe-ai-terms-modal"
      name="scribe-ai-terms-modal"
      transition="pop-out"
      :clickToClose="false"
      height="auto"
      v-model="isScribeTermsAIModalOpen"
    >
      <div class="position-relative main-modal-container">
        <div class="vm--top-right-slot" style="z-index: 1">
          <button
            class="btn p-0 p-1 mt-3 mr-3 border-0"
            @click="closeScribeTermsAIModal"
          >
            <i class="fas fa-times fa-lg"></i>
          </button>
        </div>
        <div class="card-body pt-0">
          <h1 class="text-center mt-0 mb-3 font-weight-bold">
            Start using AI Scribe
          </h1>
          <p class="text-center text-muted">
            Start using C3's AI Scribe powered by OneChart.
          </p>

          <badge class="badge-terms text-dark d-flex align-items-center p-4">
            <input
              id="termsCheckbox"
              class="pointer-cursor terms-input"
              type="checkbox"
              v-model="termsAccepted"
            />
            <label
              for="termsCheckbox"
              class="pointer-cursor mb-0 pl-3 text-left"
            >
              I agree to the
              <a
                class="text-primary"
                href="https://onechart.ai/privacy"
                target="_blank"
                >terms and conditions</a
              >
              and
              <a
                class="text-primary"
                href="https://onechart.ai/privacy"
                target="_blank"
                >privacy policy</a
              >
            </label>
          </badge>

          <badge
            class="badge-terms px-3 pt-4 pb-3 mt-4 w-100 text-left text-dark"
          >
            <p class="mb-0 font-weight-bold">
              I agree for C3 to share the following information to OneChart:
            </p>
            <ul class="d-flex flex-wrap mt-3 pt-1 mb-0">
              <li
                class="col-6 mb-2"
                v-for="field in [
                  'Email',
                  'ID',
                  'Name',
                  'Specialty Type',
                  'Practice Group',
                  'Phone Number',
                  'Region',
                ]"
                :key="field"
              >
                {{ field }}
              </li>
            </ul>
          </badge>

          <button
            class="btn btn-primary w-100 mt-4"
            :disabled="!termsAccepted"
            @click="acceptScribeAIModal"
          >
            <i class="fas fa-play-circle mr-2"></i>Start Using AI Scribe
          </button>

          <button
            class="btn btn-link text-black font-weight-bold w-100 mt-3"
            @click="closeScribeTermsAIModal"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </modal>

    <!-- Upgrade Modal -->
    <modal
      class="scribe-ai-upgrade-modal"
      name="scribe-ai-upgrade-modal"
      transition="pop-out"
      :clickToClose="false"
      height="auto"
      v-model="isScribeUpgradeAIModalOpen"
    >
      <div class="position-relative main-modal-container">
        <div class="vm--top-right-slot" style="z-index: 1">
          <button
            class="btn p-0 p-1 mt-3 mr-3 border-0"
            @click="!isNotifyingAdmin && closeScribeUpgradeAIModal()"
          >
            <i class="fas fa-times fa-lg"></i>
          </button>
        </div>
        <div class="card-body pt-0">
          <h1 class="text-center mt-0 mb-3 font-weight-bold">
            <i
              class="fas fa-2x fa-exclamation-triangle mr-2 text-deep-warning"
            ></i>
          </h1>
          <h1 class="text-center">Note Limit Reached</h1>

          <div class="alert alert5-warning text-center p-4 font-weight-bold">
            <h1 class="mb-2" style="font-size: 32px">
              {{ notesInfo.remaining }}
            </h1>
            <div>
              note{{ notesInfo.remaining === 1 ? "" : "s" }} left for your
              organization
            </div>
          </div>

          <p class="text-center text-muted mb-4 px-md-3">
            Your organization is approaching its AI Scribe note limit.
            <br />
            Contact your administrator to purchase additional notes and continue
            using this feature.
          </p>

          <button
            class="btn btn-primary w-100 mt-4"
            :disabled="isNotifyingAdmin"
            @click="notifyAdminForUpgrade()"
          >
            <i class="fas fa-bell mr-2" v-if="!isNotifyingAdmin"></i>
            <i class="fa fa-spin fa-circle-notch mr-2" v-else></i>
            Notify Administrator
          </button>

          <button
            class="btn btn-link text-black font-weight-bold w-100 mt-3"
            :disabled="isNotifyingAdmin"
            @click="closeScribeUpgradeAIModal"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { setNavigationBlock } from "@/router";
import AudioMotionAnalyzer from "audiomotion-analyzer";
import MicRecorder from "mic-recorder-to-mp3";
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";

export default {
  name: "AudioRecorder",
  props: {
    meetingId: {
      type: Number,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    demoLink: {
      type: String,
    },
  },
  data() {
    return {
      isScribeTermsAIModalOpen: false,
      isScribeUpgradeAIModalOpen: false,
      visualizer: null,
      micStream: null,
      recorder: new MicRecorder({ bitRate: 128 }),
      audioDevices: [],
      selectedDeviceId: null,
      recordedChunks: [],
      isRecording: false,
      isPaused: false,
      audioRecorded: false,
      audioQueued: false,
      audioURL: null,
      audioFile: null,
      mixAudioCtx: null,
      mixedStreamDestination: null,
      mixedStream: null,
      displayStream: null,
      meetingConfirmed: false,
      externalAudioFile: null,
      externalAudioUrl: null,
      showDemo: false,
      termsAccepted: false,
      isNotifyingAdmin: false,
      uploadType: "record",
      selectedLanguage: "en",
      languageOptions: [
        { label: "English", value: "en" },
        { label: "English (US)", value: "en-US" },
        { label: "Arabic", value: "ar" },
        { label: "Dutch", value: "nl" },
        { label: "Chinese (Simplified)", value: "zh-CN" },
        { label: "Chinese (Taiwan)", value: "zh-TW" },
        { label: "Chinese (Hong Kong)", value: "zh-HK" },
        { label: "French", value: "fr" },
        { label: "French (Canada)", value: "fr-CA" },
        { label: "German", value: "de" },
        { label: "Japanese", value: "ja" },
        { label: "Korean", value: "ko" },
        { label: "Multilingual", value: "multi" },
        { label: "Polish", value: "pl" },
        { label: "Russian", value: "ru" },
        { label: "Spanish", value: "es" },
      ],
      selectedDocument: "rba_contact_note",
      documentOptions: [
        { label: "RBA Contact Note", value: "rba_contact_note" },
        { label: "General Office Note", value: "general_office_note" },
        { label: "RBA Clinical Note", value: "rba_clinical_note" },
        { label: "RBA Supervision Note", value: "rba_supervision_note" },
        { label: "Caregiver SOAP Note", value: "caregiver_soap_note" },
        {
          label: "General Engagement Note",
          value: "c3_general_engagement_note",
        },
        { label: "General Clinical Note", value: "c3_general_clinical_note" },
        { label: "Progress SOAP Note", value: "c3_progress_soap_note" },
        {
          label: "Client Resource Handout",
          value: "c3_client_resource_handout",
        },
        { label: "Patient Handout", value: "c3_patient_handout" },
        { label: "Prescription Note", value: "c3_prescription_note" },
        { label: "Consult Note", value: "consult_note" },
        { label: "Referral Letter", value: "referral_letter" },
        { label: "Physio", value: "physio" },
      ],
    };
  },
  mounted() {
    this.getMicrophones();
    this.showDemo = !localStorage.getItem("AI_SCRIBE_DEMO");
    navigator.mediaDevices.addEventListener(
      "devicechange",
      this.handleDeviceChange
    );
  },
  beforeDestroy() {
    this.recorder.stop();
    if (this.recorder.stream) {
      this.recorder.stream.getTracks().forEach((track) => track.stop());
      this.recorder.stream = null;
    }

    this.destroyAudioSignals();

    navigator.mediaDevices.removeEventListener(
      "devicechange",
      this.handleDeviceChange
    );
    this.removeBeforeUnloadWarning();
    setNavigationBlock(); // clear navigation block
    // Clean up the object URL to prevent memory leaks
    if (this.audioURL) {
      URL.revokeObjectURL(this.audioURL);
    }
  },
  computed: {
    ...mapState({
      notesInfo: (state) => state.oneai.oneChartNotesInfo,
    }),
  },
  methods: {
    ...mapActions({
      notifyAdminforOneAiUpgrade: "oneai/notifyAdminforOneAiUpgrade",
    }),
    openScribeTermsAIModal() {
      this.isScribeTermsAIModalOpen = true;
    },
    closeScribeTermsAIModal() {
      this.isScribeTermsAIModalOpen = false;
    },
    openScribeUpgradeAIModal() {
      this.isScribeUpgradeAIModalOpen = true;
    },
    closeScribeUpgradeAIModal() {
      this.isScribeUpgradeAIModalOpen = false;
    },

    initVisualizer() {
      if (!this.$refs.visualizerContainer) {
        return;
      }
      this.visualizer = new AudioMotionAnalyzer(
        this.$refs.visualizerContainer,
        {
          gradient: "steelblue",
          overlay: true,
          bgAlpha: 0,
          height: 120,
          mode: 10,
          showScaleX: false,
          smoothing: 0,
          showScaleY: false,
          barSpace: 1,
          frameRate: 60,
        }
      );
    },

    destroyAudioSignals() {
      if (this.micStream && this.visualizer) {
        this.visualizer.disconnectInput(this.micStream, true);
        this.micStream = null;
      }
      // Stop any mixing audio context/streams we created
      this.stopMixing();
    },

    async getMicrophones() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.audioDevices = devices
          .filter(
            (d) =>
              d.kind === "audioinput" &&
              !["default", "communications"].includes(d.deviceId)
          )
          .map((d) => ({
            value: d.deviceId,
            label: d.label || `Microphone ${d.deviceId}`,
          }));
        if (this.audioDevices.length > 0)
          this.selectedDeviceId = this.audioDevices[0].value;
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Microphone Access Error",
          text: "Unable to access microphone devices. Please ensure you have granted microphone permissions.",
        });
        console.error("Mic access denied or error:", err);
      }
    },

    async handleDeviceChange() {
      await this.getMicrophones();
    },

    startScribe() {
      if (this.uploadType == "file") {
        if (!this.externalAudioFile) {
          return Swal.fire({
            icon: "warning",
            title: "No MP3 file uploaded",
            text: "Please select an MP3 file to upload.",
          });
        }
        this.audioFile = this.externalAudioFile;
        this.audioURL = this.externalAudioUrl;
        this.audioRecorded = true;
        this.isRecording = false;
        this.isPaused = false;
        this.$emit("scribeStarted");
      } else if (this.uploadType == "meeting") {
        const vm = this;
        const swalWithBootstrapButtons = Swal.mixin({
          customClass: {
            confirmButton: "btn btn-black ml-4",
            cancelButton: "btn btn-danger",
          },
          buttonsStyling: false,
        });
        swalWithBootstrapButtons
          .fire({
            title: "Use AI Scribe",
            text: "Did you record the meeting?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Use AI Scribe!",
            cancelButtonText: "No, Cancel",
            reverseButtons: true,
          })
          .then((result) => {
            if (result.isConfirmed) {
              vm.meetingConfirmed = true;
              vm.$emit("scribeStarted");
            }
          });
      } else {
        this.startRecording();
      }
    },

    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: this.selectedDeviceId
              ? { exact: this.selectedDeviceId }
              : undefined,
          },
        });
        // Only mix streams in meeting rooms (need to capture remote participant)
        // Patient profile uses mic-only — no extra AudioContext overhead
        let mixed = null;
        if (this.meetingId) {
          mixed = await this.createMixedStream(stream);
        }
        await this.recorder.start();
        if (mixed) {
          // Replace the library's mic-only source with our mixed stream
          // mic-recorder-to-mp3 calls getUserMedia internally, so we swap after start
          this.injectMixedStream(mixed);
        }
        this.isRecording = true;
        this.isPaused = false;
        this.audioURL = null;
        this.audioFile = null;
        this.recordedChunks = [];
        this.addBeforeUnloadWarning();
        setNavigationBlock(
          "You have an active or unsaved recording. Are you sure you want to leave?"
        );

        setTimeout(() => {
          this.initVisualizer();
          // visualize the mixed stream (mic + speaker) so the visualizer reflects combined audio
          const sourceStream = this.mixedStream || stream;
          this.micStream =
            this.visualizer.audioCtx.createMediaStreamSource(sourceStream);
          this.visualizer.connectInput(this.micStream);
          this.visualizer.volume = 0; // mute to prevent feedback
        }, 500);
        this.$emit("scribeStarted");
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Microphone Access Error",
          text: "Unable to access microphone devices. Please ensure you have granted microphone permissions.",
        });
        console.error("Failed to start recording:", error);
      }
    },

    async pauseRecording() {
      if (!this.isRecording) return;
      try {
        const [buffer, blob] = await this.recorder.stop().getMp3();
        const file = new File(buffer, `chunk-${Date.now()}.mp3`, {
          type: blob.type,
        });
        this.recordedChunks.push(file);
        this.isPaused = true;
        this.isRecording = false;
        // Stop drawing the visualizer when paused
        if (this.visualizer && this.micStream) {
          try {
            this.visualizer.disconnectInput(this.micStream, true);
          } catch (e) {
            console.warn("Visualizer disconnect failed on pause:", e);
          }
          this.micStream = null;
        }
      } catch (err) {
        console.warn("Pause simulation failed:", err);
      }
    },

    async resumeRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: this.selectedDeviceId
              ? { exact: this.selectedDeviceId }
              : undefined,
          },
        });
        let mixed = null;
        if (this.meetingId) {
          // Reuse existing display stream from initial recording (skip Swal + picker)
          mixed = await this.createMixedStream(stream, true);
        }
        await this.recorder.start();
        if (mixed) {
          this.injectMixedStream(mixed);
        }
        this.isRecording = true;
        this.isPaused = false;

        // Reconnect visualizer to the new mixed stream
        if (this.visualizer) {
          if (this.micStream) {
            this.visualizer.disconnectInput(this.micStream, true);
          }
          const sourceStream = this.mixedStream || stream;
          this.micStream =
            this.visualizer.audioCtx.createMediaStreamSource(sourceStream);
          this.visualizer.connectInput(this.micStream);
          this.visualizer.volume = 0;
        }
      } catch (e) {
        console.error("Resume failed:", e);
      }
    },

    /**
     * Injects a mixed audio stream into the mic-recorder-to-mp3 recorder.
     *
     * The library calls getUserMedia internally in start(), creating a mic-only source.
     * We swap that source with our mixed stream (mic + tab audio) after start resolves.
     *
     * Library internals used:
     * - this.recorder.microphone: MediaStreamSource node
     * - this.recorder.processor: ScriptProcessor (MP3 encoding)
     * - this.recorder.context: AudioContext
     */
    injectMixedStream(mixedStream) {
      try {
        if (
          !mixedStream ||
          !this.recorder.microphone ||
          !this.recorder.processor ||
          !this.recorder.context
        ) {
          return;
        }
        // Disconnect the library's mic-only source
        this.recorder.microphone.disconnect();
        // Create a new source from our mixed stream and connect to the processor
        const mixedSource =
          this.recorder.context.createMediaStreamSource(mixedStream);
        mixedSource.connect(this.recorder.processor);
        // Keep reference so the library's stop() can still clean up
        this.recorder.microphone = mixedSource;
      } catch (e) {
        console.warn("Could not inject mixed stream into recorder:", e);
      }
    },

    async createMixedStream(micStream, isResume = false) {
      // If browser doesn't support AudioContext or mixing, just return mic stream
      if (!window.AudioContext && !window.webkitAudioContext) return micStream;

      try {
        // On resume, keep the existing display stream; only rebuild the mixing context
        const existingDisplayStream = isResume ? this.displayStream : null;
        this.stopMixing(isResume);

        // Only request tab audio capture for meeting rooms (remote participants)
        // In-person (patient profile) sessions just use the mic
        let displayStream = existingDisplayStream || null;
        if (this.meetingId && !displayStream) {
          try {
            await Swal.fire({
              icon: "info",
              title: "Capture Meeting Audio",
              html: 'To record both voices, click <b>"Allow"</b> in the next dialog and make sure <b>"Also allow tab audio"</b> is turned on.',
              confirmButtonText: "Continue",
              confirmButtonColor: "#e27c1d",
            });

            displayStream = await navigator.mediaDevices.getDisplayMedia({
              video: { width: 1, height: 1, frameRate: 1 }, // minimal video (required by API)
              audio: true, // captures tab audio output
              preferCurrentTab: true, // pre-selects current tab in picker
              selfBrowserSurface: "include",
              monitorTypeSurfaces: "exclude", // hide screen/window options, show only tabs
            });

            // We only need the audio track — stop the video track immediately
            displayStream.getVideoTracks().forEach((t) => t.stop());
            this.displayStream = displayStream;
          } catch (e) {
            // User denied or browser doesn't support — fall back to mic only
            console.warn(
              "Tab audio capture denied or unavailable, recording mic only:",
              e.name
            );
          }
        }

        this.mixAudioCtx = new (window.AudioContext ||
          window.webkitAudioContext)();
        const dest = this.mixAudioCtx.createMediaStreamDestination();

        // Connect microphone
        const micSource = this.mixAudioCtx.createMediaStreamSource(micStream);
        micSource.connect(dest);

        // Connect tab audio if available (captures remote participant voice)
        if (displayStream && displayStream.getAudioTracks().length > 0) {
          const tabSource =
            this.mixAudioCtx.createMediaStreamSource(displayStream);
          tabSource.connect(dest);
        }

        this.mixedStreamDestination = dest;
        this.mixedStream = dest.stream;
        return this.mixedStream;
      } catch (err) {
        console.error(
          "Error creating mixed stream, falling back to mic only",
          err
        );
        this.stopMixing();
        return micStream;
      }
    },

    stopMixing(keepDisplayStream = false) {
      try {
        if (this.mixedStreamDestination) {
          this.mixedStreamDestination = null;
        }
        if (this.mixAudioCtx) {
          try {
            this.mixAudioCtx.close();
          } catch (e) {
            // ignore
          }
          this.mixAudioCtx = null;
        }
        if (this.mixedStream) {
          this.mixedStream.getTracks().forEach((t) => t.stop());
          this.mixedStream = null;
        }
        if (this.displayStream && !keepDisplayStream) {
          this.displayStream.getTracks().forEach((t) => t.stop());
          this.displayStream = null;
        }
      } catch (e) {
        console.warn("stopMixing error", e);
      }
    },

    async stopRecording() {
      this.audioRecorded = true;
      if (this.isRecording) {
        try {
          const [buffer, blob] = await this.recorder.stop().getMp3();
          const file = new File(buffer, `chunk-${Date.now()}.mp3`, {
            type: blob.type,
          });
          this.recordedChunks.push(file);
        } catch (e) {
          console.error("Final recording chunk failed:", e);
        }
      }
      this.isRecording = false;
      this.isPaused = false;

      this.destroyAudioSignals();

      this.concatMp3Chunks();
      this.removeBeforeUnloadWarning();
      setNavigationBlock(); // clear navigation block
      this.completeScribe();
    },

    // Concatenate raw MP3 chunk Blobs — MP3 frames are self-delimiting so
    // simple binary concat produces a valid MP3, eliminating the need for
    // client-side PCM decode + re-encode (lamejs).
    concatMp3Chunks() {
      if (!this.recordedChunks.length) return;

      const merged = new Blob(this.recordedChunks, { type: "audio/mpeg" });
      const filename = `AI_note_${Date.now()}.mp3`;

      this.audioFile = new File([merged], filename, { type: "audio/mpeg" });
      this.audioURL = URL.createObjectURL(merged);
    },

    closeDemo() {
      localStorage.setItem("AI_SCRIBE_DEMO", true);
      this.showDemo = false;
    },

    checkScribeAiTerms() {
      if (!this.notesInfo.remaining) {
        this.openScribeUpgradeAIModal();
        return;
      }
      this.termsAccepted = !!localStorage.getItem("AI_SCRIBE_TERMS_ACCEPTED");
      if (this.termsAccepted) {
        this.startScribe();
      } else {
        this.openScribeTermsAIModal();
      }
    },

    notifyAdminForUpgrade() {
      if (this.isNotifyingAdmin) {
        return;
      }
      this.isNotifyingAdmin = true;
      this.notifyAdminforOneAiUpgrade().then((success) => {
        this.isNotifyingAdmin = false;
        if (success) {
          Swal.fire({
            title: "Notification Sent",
            text: "Your administrator has been notified about the AI Scribe upgrade.",
            icon: "success",
          });
          this.closeScribeUpgradeAIModal();
        }
      });
    },
    recordingQueued() {
      this.audioQueued = true;
    },

    acceptScribeAIModal() {
      if (this.termsAccepted) {
        localStorage.setItem("AI_SCRIBE_TERMS_ACCEPTED", true);
        this.closeScribeTermsAIModal();
        this.startScribe();
      }
    },

    async completeScribe() {
      this.$emit(
        "recordingComplete",
        {
          audioFile: this.audioFile,
          audioURL: this.audioURL,
          note_type: this.selectedDocument,
          language: this.selectedLanguage,
          meetingId: this.meetingId,
          uploadType: this.uploadType,
        },
        this.recordingQueued
      );
    },

    updateSelectedAudioFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Ensure it's an MP3
      if (file.type !== "audio/mpeg") {
        return Swal.fire({
          title: "Invalid File Type",
          text: "Please select a valid MP3 file.",
          icon: "error",
        });
      }

      this.externalAudioFile = file;
      this.externalAudioUrl = URL.createObjectURL(file);
      this.uploadType = "file";
    },

    watchDemo() {
      window.open(this.demoLink, "_blank");
    },

    addBeforeUnloadWarning() {
      window.addEventListener("beforeunload", this.handleBeforeUnload);
    },

    removeBeforeUnloadWarning() {
      window.removeEventListener("beforeunload", this.handleBeforeUnload);
    },

    handleBeforeUnload(event) {
      event.preventDefault();
      event.returnValue = ""; // Chrome requires returnValue to be set
    },
  },
};
</script>

<style scoped lang="scss">
.badge-terms {
  white-space: normal;
}
.audio-visualizer {
  width: 100%;
  height: 121px;
  border: 1px solid #94c6fb;
  border-radius: 8px;
  background: #f5f9ff;
}

.audio-recorder-container {
  .badge {
    overflow-wrap: break-word;
    white-space: normal;
  }

  .radius-16 {
    border-radius: 16px;
  }

  @media (min-width: 408px) {
    .min-296 {
      min-width: 296px;
    }
  }

  .external-audio-result {
    height: calc(1.5em + 1rem + 2px);
  }
  .radius-24 {
    border-radius: 24px;
  }

  .custom-file {
    max-width: 310px;
    height: 47px;
  }

  .file-name-label {
    padding-right: 80px;
    max-width: 300px;
  }
  .close-demo {
    position: absolute;
    padding: 4px 8px;
    top: 16px;
    right: 4px;
    font-size: 32px;
  }

  .badge-primary-light {
    background-color: #9dbbe342;
    padding: 24px 46px 24px 24px;

    @media (max-width: 767px) {
      padding: 12px;
    }
  }

  .demo-image-container {
    border-radius: 50%;
    background-color: #99bae691;
    padding: 12px;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;

    .demo-img {
      color: #1a6ad4;
      font-size: 2em;
    }
  }

  .powered-by {
    color: #676b72;
    font-size: 13px;
  }

  .link-color {
    color: #985ae3;
  }

  .controls-badge {
    color: #004085;
    background-color: #cce5ff;
    border: 1px solid#b8daff;
  }

  .recording-status {
    .recording-dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: 1px solid #222;
      background-color: #c31527;
      opacity: 0.5;

      &.recording-active {
        opacity: 1;
        animation: blink 1s infinite;
      }
    }

    .signal-bars {
      height: 20px;
      display: flex;
      gap: 2px;
      align-items: flex-end;

      .bar {
        width: 4px;
        background-color: #004085;
      }
      .bar:nth-child(1) {
        height: 5px;
      }
      .bar:nth-child(2) {
        height: 7px;
      }
      .bar:nth-child(3) {
        height: 9px;
      }
      .bar:nth-child(4) {
        height: 12px;
      }
      .bar:nth-child(5) {
        height: 15px;
      }
      &.recording-active {
        .bar {
          animation: bounce 1s infinite ease-in-out;
        }
        .bar:nth-child(1) {
          animation-delay: 0s;
        }
        .bar:nth-child(2) {
          animation-delay: 0.1s;
        }
        .bar:nth-child(3) {
          animation-delay: 0.2s;
        }
        .bar:nth-child(4) {
          animation-delay: 0.3s;
        }
        .bar:nth-child(5) {
          animation-delay: 0.4s;
        }
      }
    }
  }

  .scribe-ai-terms-modal,
  .scribe-ai-upgrade-modal {
    .v--modal {
      max-height: 80vh;
    }
  }

  @keyframes blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  @keyframes bounce {
    0%,
    100% {
      height: 5px;
    }
    50% {
      height: 20px;
    }
  }
}
</style>
